import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { TrendingUp, Loader2, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';

type Mode = 'login' | 'signup' | 'forgot';

const MODE_COPY: Record<Mode, { title: string; subtitle: string; cta: string }> = {
  login:  { title: 'Welcome back',     subtitle: 'Log in to your planner',            cta: 'Log in' },
  signup: { title: 'Create account',   subtitle: 'Start planning in minutes',         cta: 'Sign up' },
  forgot: { title: 'Reset password',   subtitle: "We'll email you a reset link",      cta: 'Send reset link' },
};

export function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, isRecoverySession } = useAuth();

  // Allow deep-linking to a starting mode via ?mode=signup.
  const initialMode = (new URLSearchParams(location.search).get('mode') as Mode) || 'login';
  const [mode, setMode] = useState<Mode>(
    initialMode === 'signup' || initialMode === 'forgot' ? initialMode : 'login'
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The auth page is always dark, matching the always-dark landing page. Mirrors
  // LandingPage's approach: pin <html> to the dark class while mounted, then
  // restore the previous class on unmount so the user's in-app theme is untouched.
  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains('light');
    if (wasLight) {
      html.classList.remove('light');
      html.classList.add('dark');
    }
    return () => {
      if (wasLight) {
        html.classList.remove('dark');
        html.classList.add('light');
      }
    };
  }, []);

  // Where to go after a successful login. Honour a `from` redirect if present.
  const from = (location.state as { from?: string } | null)?.from ?? '/app';

  // A recovery-only session (arrived via a reset link) must set a new password
  // first — send it to the reset page, never into the app. This also catches
  // legacy reset emails that still point at /login.
  if (isRecoverySession) {
    return <Navigate to="/reset-password" replace />;
  }

  // Already logged in → bounce to the app.
  if (session) {
    return <Navigate to={from} replace />;
  }

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);

    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate(from, { replace: true });
        return;
      }

      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/app` },
        });
        if (error) throw error;
        // With email confirmation on (Supabase default), no session is returned
        // until the user clicks the link in their inbox.
        if (data.session) {
          navigate(from, { replace: true });
          return;
        }
        setNotice(`Check your email. We sent a confirmation link to ${email}. Confirm it, then log in.`);
        return;
      }

      // forgot
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setNotice(`If an account exists for ${email}, a password reset link is on its way.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copy = MODE_COPY[mode];

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center px-4 py-12">
      {/* Brand */}
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="p-2 bg-[rgb(var(--overlay)/0.06)] rounded-xl border border-slate-700">
          <TrendingUp className="w-5 h-5 text-slate-400" />
        </div>
        <div>
          <span className="font-display text-lg font-bold text-slate-100">MyCrypto</span>
          <span className="font-display text-lg font-bold text-gradient">Planner</span>
        </div>
      </Link>

      <div className="w-full max-w-sm bg-slate-950 border border-slate-700 rounded-2xl p-8">
        <h1 className="font-display text-xl font-bold text-slate-100">{copy.title}</h1>
        <p className="text-sm text-slate-400 mt-1">{copy.subtitle}</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="label-caps block mb-1.5" htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-[var(--color-bronze-bright)] transition-colors"
              placeholder="you@example.com"
            />
          </div>

          {mode !== 'forgot' && (
            <div>
              <label className="label-caps block mb-1.5" htmlFor="auth-password">Password</label>
              <input
                id="auth-password"
                type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-[var(--color-bronze-bright)] transition-colors"
                placeholder="••••••••"
              />
            </div>
          )}

          {mode === 'login' && (
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className="text-xs text-slate-400 hover:text-bronze-bright transition-colors"
              >
                Forgot password?
              </button>
            </div>
          )}

          {error && (
            <div className="text-xs rounded-lg px-3 py-2 bg-[var(--color-negative)]/10 border border-[var(--color-negative)]/30 text-negative">
              {error}
            </div>
          )}

          {notice && (
            <div className="text-xs rounded-lg px-3 py-2 bg-[var(--color-bronze)]/10 border border-[var(--color-bronze)]/30 text-bronze flex items-start gap-2">
              <Mail className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{notice}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-transform hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
            style={{ backgroundColor: 'var(--color-bronze-bright)', color: '#0c0e12' }}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {copy.cta}
          </button>
        </form>

        {/* Mode switcher */}
        <div className="mt-6 text-center text-sm text-slate-400">
          {mode === 'login' && (
            <>
              New here?{' '}
              <button onClick={() => switchMode('signup')} className="text-bronze-bright hover:underline">
                Create an account
              </button>
            </>
          )}
          {mode === 'signup' && (
            <>
              Already have an account?{' '}
              <button onClick={() => switchMode('login')} className="text-bronze-bright hover:underline">
                Log in
              </button>
            </>
          )}
          {mode === 'forgot' && (
            <button onClick={() => switchMode('login')} className="text-bronze-bright hover:underline">
              Back to log in
            </button>
          )}
        </div>
      </div>

      <Link to="/" className="mt-6 text-xs text-slate-500 hover:text-slate-300 transition-colors">
        ← Back to home
      </Link>
    </div>
  );
}
